import React, { useMemo } from 'react';
import { WidgetContainer, MainMetric } from '@frontend/app/refresh-components';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { BoxIcon, ArrowTrendUpIcon } from '@revfluence/fresh-icons/solid/esm';
import { formatDistanceStrict } from 'date-fns';
import { isNil } from 'lodash';
import { useGetOrdersTypeBreakdown } from '@frontend/applications/ProductFulfillmentApp/hooks/useGetOrdersTypeBreakdown';
import { usePFADashboardContext } from '../containers/ProductFulfillmentDashboard/ProductFulfillmentDashboardContext';
import { ProjectWidgetContainer } from '../containers/Projects/OverviewPage/Widgets/common/ProjectWidgetContainer';

interface PFATotalOrdersCreatedProps {
  className?: string;
  v2?: boolean;
  programId?: number;
}

export default function PFATotalOrdersCreated({ className, v2 = false, programId }: PFATotalOrdersCreatedProps) {
  const { dateRangeSettings } = usePFADashboardContext();

  const startDate = dateRangeSettings.dateRange?.startDate;
  const endDate = dateRangeSettings.dateRange?.endDate;

  const distanceInWords = formatDistanceStrict(startDate, endDate);

  const { isOrdersTypeBreakdownLoading, ordersTypeBreakdown } = useGetOrdersTypeBreakdown({
    variables: {
      dashboardFilter: {
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        programId,
      },
    },
  });

  const totalOrders = useMemo(() => {
    let total = 0;
    if (ordersTypeBreakdown?.creatorSelected) {
      total += ordersTypeBreakdown.creatorSelected;
    }
    if (ordersTypeBreakdown?.giftOrders) {
      total += ordersTypeBreakdown.giftOrders;
    }
    if (ordersTypeBreakdown?.offlineProcessed) {
      total += ordersTypeBreakdown.offlineProcessed;
    }
    return total;
  }, [ordersTypeBreakdown]);

  const loadingContent = (
    <div className="w-full h-full flex flex-col justify-center items-center">
      <Skeleton className="h-6 w-full mb-4" />
      <Skeleton className="h-6 w-full mb-4" />
      <Skeleton className="h-6 w-full mb-4" />
    </div>
  );
  const mainContent = (
    <MainMetric
      value={totalOrders}
      size="medium"
      icon={null}
      metricType="count"
      trend={ordersTypeBreakdown?.trendIncrease}
      subHeading={!isNil(ordersTypeBreakdown?.trendIncrease) ? `in last ${distanceInWords}` : undefined}
      className="p-2 pt-0 -mt-2"
    />
  );

  if (v2) {
    if (isOrdersTypeBreakdownLoading) {
      return (
        <ProjectWidgetContainer
          title="Total Orders Created"
          icon={<BoxIcon className="text-blue-6" />}
          tooltip="Total number of orders created across all channels"
          className="w-full"
        >
          <div className="flex flex-col gap-2">
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-4 w-24" />
          </div>
        </ProjectWidgetContainer>
      );
    }

    const trendText = !isNil(ordersTypeBreakdown?.trendIncrease)
      ? (
        <div className="inline-flex items-center gap-1">
          <div className="px-1 bg-white rounded-lg border border-gray-200 flex items-center gap-1">
            <ArrowTrendUpIcon className="w-3 h-3 text-gray-500" />
            <span className="text-gray-500 text-xs font-normal leading-tight">{ordersTypeBreakdown.trendIncrease}%</span>
          </div>
          <span className="text-gray-500 text-xs font-normal leading-tight">in last {distanceInWords}</span>
        </div>
      )
      : `in last ${distanceInWords}`;

    return (
      <ProjectWidgetContainer
        title="Total Orders Created"
        icon={<BoxIcon className="text-blue-6" />}
        tooltip="Total number of orders created across all channels"
        className="w-full"
      >
        <div className="flex flex-col justify-center items-start gap-2">
          <div className="self-stretch inline-flex justify-start items-center gap-2">
            <div className="flex justify-start items-end gap-1">
              <div className="justify-start text-gray-800 text-3xl font-medium font-['Inter'] leading-[48px]">
                {totalOrders || 0}
              </div>
            </div>
          </div>
          <div className="self-stretch inline-flex justify-start items-center gap-1">
            <div className="justify-center text-gray-500 text-xs font-normal font-['Inter'] leading-tight">
              {trendText}
            </div>
          </div>
        </div>
      </ProjectWidgetContainer>
    );
  }

  return (
    <WidgetContainer
      widgetTitle="Total Orders Created"
      className={`${className}`}
      bgColor="bg-[#003F5B]"
      widgetIcon={<BoxIcon className="text-white" fontSize={16} />}
    >
      {isOrdersTypeBreakdownLoading ? loadingContent : mainContent}
    </WidgetContainer>
  );
}
